// Reports → Cold Chain grids (table + CSV + Excel share them). Pure.
// Columns follow the server's day rows: min/max may include the value carried in from before
// midnight (shown as given); % in range is of the covered time, so "Covered min" lets a partial
// day (today) reconcile: in range + out of range + no data = covered.
import { dateTimeInTz, localDateTimeInTz, addDays } from '@/features/staff/attendanceModel';
import { csvText } from '@/lib/export';
import { changeLines } from './coldPointHistoryModel';
import { formatRange } from './coldChainModel';

const fmt1 = (v) => (typeof v === 'number' ? v.toFixed(1) : '');

const pad2 = (n) => String(n).padStart(2, '0');
// "YYYY-MM-DD HH:mm:ss +03:00": org wall clock with seconds and the zone's offset at that instant (two changes in one
// minute stay apart; a DST-repeated hour is unambiguous). Twin of wallTimeWithOffset in server/src/coldchain/scheduled-report.ts.
export function wallTimeWithOffset(iso, tz) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'longOffset',
  }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  const off = /^GMT([+-]\d{2}:\d{2})$/.exec(parts.timeZoneName ?? '')?.[1] ?? '+00:00';   // "GMT" alone is UTC
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)} ${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)} ${off}`;
}

// The day's range in the CSV: `2.0–8.0 °C` / `≥ 2.0 °C` / `≤ 8.0 °C` / `—`, ` *` when it changed during the day.
// Twin of rangeLabel in server/src/coldchain/scheduled-report.ts (same bytes in the download and the emailed CSV).
export function rangeLabel(min, max, changed = false) {
  const base = typeof min === 'number' && typeof max === 'number' ? `${min.toFixed(1)}–${max.toFixed(1)} °C`
    : typeof min === 'number' ? `≥ ${min.toFixed(1)} °C`
      : typeof max === 'number' ? `≤ ${max.toFixed(1)} °C` : '—';
  return changed ? `${base} *` : base;
}

// "Rear (archived)" — a cold point archived inside the report window still reports.
export const pointLabel = (p, t) => (p.archived ? `${p.name} (${t('coldChain.report.archived', 'archived')})` : p.name);

// csv mode: the server's literal English headers and an empty cell where there is no percentage
// (server/src/coldchain/scheduled-report.ts), so the download and the emailed CSV are the same bytes.
const english = (_key, def) => def;

export function dayGrid(report, tr, { csv = false } = {}) {
  const t = csv ? english : tr;
  const headers = [
    t('coldChain.report.place', 'Vehicle / place'), t('coldChain.report.point', 'Cold point'), t('coldChain.report.day', 'Day'),
    t('coldChain.report.range', 'Range'),
    t('coldChain.report.min', 'Min °C'), t('coldChain.report.max', 'Max °C'), t('coldChain.report.avg', 'Avg °C'),
    t('coldChain.report.inRange', '% in range'), t('coldChain.report.covered', 'Covered min'),
    t('coldChain.report.offMinutes', 'Monitoring off min'),
    t('coldChain.report.outOfRange', 'Out of range min'), t('coldChain.report.gapMinutes', 'No-data min'),
    t('coldChain.report.excursions', 'Excursions'), t('coldChain.report.excursionMinutes', 'Excursion min'),
  ];
  const rows = [];
  for (const p of report?.points ?? []) {
    for (const d of p.days) {
      rows.push([
        p.deviceName, pointLabel(p, t), d.day,
        csv ? rangeLabel(d.rangeMin ?? null, d.rangeMax ?? null, !!d.rangeChanged)
          : `${formatRange(d.rangeMin ?? null, d.rangeMax ?? null)}${d.rangeChanged ? ' *' : ''}`,
        fmt1(d.min), fmt1(d.max), fmt1(d.avg),
        d.inRangePct === null && !csv ? t('coldChain.report.monitoringOff', 'Monitoring off') : fmt1(d.inRangePct),
        d.coveredMinutes, d.offMinutes ?? 0, d.outOfRangeMinutes, d.gapMinutes, d.excursionCount, d.excursionMinutes,
      ]);
    }
  }
  return { headers, rows };
}

export function excursionGrid(report, tr, { csv = false } = {}) {
  const t = csv ? english : tr;
  const tz = report?.timezone;
  const headers = [
    t('coldChain.report.place', 'Vehicle / place'), t('coldChain.report.point', 'Cold point'), t('coldChain.report.kind', 'Type'),
    t('coldChain.report.started', 'Started'), t('coldChain.report.ended', 'Ended'), t('coldChain.report.endedBy', 'Ended by'),
    t('coldChain.report.minutes', 'Minutes'),
    t('coldChain.report.peak', 'Peak °C'), t('coldChain.report.direction', 'Direction'), t('coldChain.report.movement', 'Movement'),
  ];
  const archivedIds = new Set((report?.points ?? []).filter((p) => p.archived).map((p) => p.id));
  const rows = (report?.excursions ?? []).map((e) => [
    e.deviceName,
    archivedIds.has(e.coldPointId) ? `${e.coldPointName} (${t('coldChain.report.archived', 'archived')})` : e.coldPointName,
    e.kind === 'no_data' ? t('coldChain.kind.noData', 'No data') : t('coldChain.kind.outOfRange', 'Out of range'),
    dateTimeInTz(e.startedAt, tz),
    e.endedAt ? dateTimeInTz(e.endedAt, tz) : t('coldChain.ongoing', 'Ongoing'),
    e.endedAt ? endReasonLabel(e.endedReason ?? null, t) : '',
    e.endedAt ? Math.round((Date.parse(e.endedAt) - Date.parse(e.startedAt)) / 60_000) : '',
    fmt1(e.peakValue),
    e.direction === 'above' ? t('coldChain.report.above', 'Above range') : e.direction === 'below' ? t('coldChain.report.below', 'Below range') : '',
    e.startedMoving === true ? t('coldChain.moving', 'Moving') : e.startedMoving === false ? t('coldChain.stopped', 'Stopped') : '',
  ]);
  return { headers, rows };
}

// Excursion end reason in the user's words. Literal t() calls so the i18n guard sees each key.
export function endReasonLabel(reason, t) {
  if (reason === 'in_range') return t('coldChain.endReason.inRange', 'Back in range');
  if (reason === 'monitoring_stopped') return t('coldChain.endReason.monitoringStopped', 'Monitoring stopped');
  if (reason === 'settings_changed') return t('coldChain.endReason.settingsChanged', 'Settings changed');
  if (reason === 'no_data_ended') return t('coldChain.endReason.noDataEnded', 'Data resumed');
  return '';
}

// Settings-changes record. On screen: human labels (changeLines). In the CSV: the server's raw
// `field: from → to` text, byte-for-byte (server/src/coldchain/scheduled-report.ts).
const ASSUMED_CSV = 'Settings before migration assumed from current values';
const csvValue = (v) => (v === null || v === undefined ? '—' : Array.isArray(v) ? v.join(',') : String(v));
const rawChanged = (c) => (c.provenance === 'assumed' ? ASSUMED_CSV
  : c.kind === 'archive' ? 'Archived'
    : Object.entries(c.changes ?? {}).map(([k, { from, to }]) => `${k}: ${csvValue(from)} → ${csvValue(to)}`).join('; '));

export function changesGrid(report, t, { csv = false } = {}) {
  const tz = report?.timezone;
  const headers = csv
    ? ['Vehicle / place', 'Cold point', 'When', 'By', 'Changed', 'Reason']
    : [t('coldChain.report.place', 'Vehicle / place'), t('coldChain.report.point', 'Cold point'), t('coldChain.report.when', 'When'),
      t('coldChain.report.by', 'By'), t('coldChain.report.changed', 'Changed'), t('coldChain.report.reason', 'Reason')];
  const humanChanged = (c) => (c.provenance === 'assumed'
    ? t('coldChain.report.assumedChange', 'Settings before migration assumed from current values')
    : c.kind === 'archive' ? t('coldChain.history.archived', 'Archived') : changeLines(c, t).join('; '));
  const rows = (report?.changes ?? []).map((c) => [
    c.deviceName, c.coldPointName, csv ? wallTimeWithOffset(c.at, tz) : dateTimeInTz(c.at, tz), c.byEmail ?? '', csv ? rawChanged(c) : humanChanged(c), c.reason ?? '',
  ]);
  return { headers, rows };
}

// The downloaded CSV: per-day section, a blank line, the excursion record (the audit item), a blank
// line, then the settings changes. Same bytes the server emails for a scheduled report
// (server/src/coldchain/scheduled-report.ts): every section is built in csv mode.
export function reportCsv(report, t) {
  const days = dayGrid(report, t, { csv: true });
  const exc = excursionGrid(report, t, { csv: true });
  const chg = changesGrid(report, t, { csv: true });
  return `${csvText(days.headers, days.rows)}\r\n\r\n${csvText(exc.headers, exc.rows).slice(1)}\r\n\r\n${csvText(chg.headers, chg.rows).slice(1)}`;
}

// Readings dropped while monitoring was off (admin/manager audit export). Time carries seconds and the UTC offset.
export function excludedCsv(rows, tz, t) {
  const headers = [t('coldChain.report.place', 'Vehicle / place'), t('coldChain.report.point', 'Cold point'), t('coldChain.report.time', 'Time'),
    t('coldChain.report.celsius', '°C'), t('coldChain.report.version', 'Version'), t('coldChain.report.reason', 'Reason')];
  return csvText(headers, (rows ?? []).map((r) => [r.deviceName, r.coldPointName, wallTimeWithOffset(r.at, tz), r.value ?? '', r.versionNo ?? '—', r.reason ?? '']));
}

// ---- Report charts: the report's org-day range as a history window ----
const DAY_MS = 86_400_000;
export const HISTORY_CAP_MS = 31 * DAY_MS;   // server MAX_WINDOW_DAYS for /points/:id/history

/** The instant an org-local day starts (00:00 wall clock in `tz`). */
export function dayStartMs(day, tz) {
  const target = Date.parse(`${day}T00:00:00Z`);
  let guess = target;
  for (let i = 0; i < 3; i += 1) {
    const wall = Date.parse(`${localDateTimeInTz(new Date(guess).toISOString(), tz)}:00Z`);
    if (wall === target) break;
    guess -= wall - target;
  }
  return guess;
}

/**
 * Start of fromDay to end of toDay in the org timezone, clipped to now.
 * `tooLong` = the span exceeds the history cap (no charts, show a note); `empty` = nothing to draw yet.
 */
export function chartWindow(fromDay, toDay, tz, now = Date.now()) {
  const fromMs = dayStartMs(fromDay, tz);
  const toMs = Math.min(dayStartMs(addDays(toDay, 1), tz), now);
  return { fromMs, toMs, tooLong: toMs - fromMs > HISTORY_CAP_MS, empty: toMs <= fromMs };
}
